"use client";
import { useGSAP } from "@gsap/react";
import { cva, type VariantProps } from "class-variance-authority";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import type React from "react";
import { useRef } from "react";
import { TextD2C } from "../../elements/text-d2c/text";
import { Column, Container, Row } from "../../layout/grid";
import { cn } from "../../lib/utils";
import { CounterMetric } from "./counter-metric";
import type { CounterInspectorProps } from "./counter-section.preview";

gsap.registerPlugin(ScrollTrigger, useGSAP);

const MAX_COUNTER_ITEMS = 4;

const counterVariants = cva("", {
  variants: {
    columnWidth: {
      8: "col-span-10 col-start-2 md:col-span-8 md:col-start-3",
      10: "col-span-10 col-start-2",
      12: "col-span-12",
    },
    columns: {
      1: "grid grid-cols-1",
      2: "grid grid-cols-1 md:grid-cols-2",
      3: "grid grid-cols-1 lg:grid-cols-3",
      4: "grid grid-cols-1 lg:grid-cols-4",
    },
    backgroundColor: {
      black: "bg-black",
      white: "bg-white",
      whitesmoke: "bg-whitesmoke",
    },
    color: {
      black: "text-black",
      white: "text-white",
      alabastergrey: "text-alabastergrey",
      darkteal: "text-darkteal",
    },
  },
  defaultVariants: {
    columnWidth: 12,
    columns: 3,
    backgroundColor: "whitesmoke",
    color: "darkteal",
  },
});

type CounterItem = {
  sys?: { id?: string };
  value: string | number;
  label: string;
  prefix?: string;
  suffix?: string;
  inspectorProps?: CounterInspectorProps;
};

type CounterColumns = 1 | 2 | 3 | 4;

type CounterVariantProps = VariantProps<typeof counterVariants>;

export interface CounterProps extends Omit<CounterVariantProps, "columns"> {
  sys?: { id?: string };
  paddingTop?: "none" | "small" | "medium" | "large";
  paddingBottom?: "none" | "small" | "medium" | "large";
  items: CounterItem[];
  footnote?: string;
  showMarkers?: boolean;
  inspectorProps?: CounterInspectorProps;
}

export function Counter({
  columnWidth,
  backgroundColor,
  color,
  paddingTop = "large",
  paddingBottom = "large",
  items,
  footnote,
  showMarkers,
  inspectorProps,
}: Readonly<CounterProps>) {
  const visibleItems = items.slice(0, MAX_COUNTER_ITEMS);
  const columns: CounterColumns =
    visibleItems.length === 0
      ? 1
      : (Math.min(visibleItems.length, MAX_COUNTER_ITEMS) as CounterColumns);

  const columnClasses = counterVariants({
    columnWidth,
    columns: null,
    backgroundColor: null,
    color: null,
  });

  const gridClasses = counterVariants({
    columnWidth: null,
    columns,
    backgroundColor: null,
    color: null,
  });

  const sectionClasses = counterVariants({
    columnWidth: null,
    columns: null,
    backgroundColor,
    color,
  });

  const container = useRef<HTMLDivElement>(null);
  useGSAP(
    () => {
      initGsapAnimation({
        showMarkers,
        ref: container,
      });
    },
    {
      dependencies: [showMarkers],
      scope: container,
      revertOnUpdate: true,
    }
  );

  const textColor: "black" | "white" =
    backgroundColor === "black" ? "white" : "black";

  return (
    <Container
      className={cn(sectionClasses, "component-counter")}
      fullWidth={true}
      paddingBottom={paddingBottom}
      paddingTop={paddingTop}
      testId="counter"
      {...inspectorProps?.container}
    >
      <Row className="font-brand font-normal">
        <Column className={cn(columnClasses)}>
          <div className={cn(gridClasses, "gap-unit")} ref={container}>
            {visibleItems.map((item, index) => (
              <div
                className={cn(
                  "flex flex-col gap-5 text-center",
                  "counter-item"
                )}
                key={`${item.label}-${index}`}
                {...item.inspectorProps?.container}
              >
                <CounterMetric
                  prefix={item.prefix}
                  suffix={item.suffix}
                  value={item.value}
                />
                <TextD2C color={textColor} style="uppercase" variant="p">
                  {item.label}
                </TextD2C>
              </div>
            ))}
          </div>
          {footnote && (
            <div className="mt-10 text-center">
              <TextD2C color={textColor} style={"regular"} variant="h6">
                {footnote}
              </TextD2C>
            </div>
          )}
        </Column>
      </Row>
    </Container>
  );
}

const initGsapAnimation = (props: {
  showMarkers?: boolean;
  ref: React.RefObject<HTMLDivElement | null>;
}) => {
  const item = props.ref.current;

  if (!item) {
    return;
  }

  const items = item.querySelectorAll(".counter-item [data-animate-numbers]");

  return gsap.from(items, {
    innerText: 0,
    duration: 2,
    ease: "power1.in",
    snap: {
      innerText: 1,
    },
    stagger: {
      each: 1,
      onUpdate() {},
    },

    scrollTrigger: {
      trigger: item,
      markers: props.showMarkers,
      start: "top 90%",
      end: "bottom 10%",
      // onEnter, onLeave, onEnterBack, and onLeaveBack
      toggleActions: "play pause resume pause",
    },
  });
};
